import { get } from 'lodash';
import subDays from 'date-fns/subDays';
import pluralize from 'pluralize';
import collections from '../util/collections';
import ApiService from './api-service';
import {
  CHASEFEED,
  PAID_INVOICES,
  CHASING_INVOICES,
  NOT_CHASING_INVOICES,
  MISSING_EMAIL,
  MISSING_NAME,
  COLLECTED_INVOICES,
  AGED_DEBT,
  READY_FOR_COLLECTION,
  COLLECTING_INVOICES,
  PENDING_INVOICES,
  SCHEDULE,
  IN_DISPUTE,
  PAYMENT_PLAN,
  LATE_PAYMENT_FEES,
  NO_LATE_PAYMENT_FEES,
  EARLY_PAYMENT_DISCOUNTS,
  NO_EARLY_PAYMENT_DISCOUNTS,
  WAITING_CONTRACT,
  INVOICEIDS,
  hasFilter,
} from '../util/filters';
import { formatDate } from '../util/time';
import batchFetch from '../util/batchFetch';
import subMinutes from 'date-fns/subMinutes';
import { toast } from '../components/ToastContainer';
import { ALL_COLLECTIONS_INVOICES } from '@/util/constants';

const InvoiceService = {
  getInvoiceFilter(filter) {
    return {
      paymentPlan: hasFilter(filter?.invoiceFilters, PAYMENT_PLAN) ? true : undefined,
      latePaymentFees: hasFilter(filter?.invoiceFilters, LATE_PAYMENT_FEES) ? true : undefined,
      noLatePaymentFees: hasFilter(filter?.invoiceFilters, NO_LATE_PAYMENT_FEES) ? true : undefined,
      earlyPaymentDiscounts: hasFilter(filter?.invoiceFilters, EARLY_PAYMENT_DISCOUNTS)
        ? true
        : undefined,
      noEarlyPaymentDiscounts: hasFilter(filter?.invoiceFilters, NO_EARLY_PAYMENT_DISCOUNTS)
        ? true
        : undefined,
      inDispute: hasFilter(filter?.invoiceFilters, IN_DISPUTE) ? true : undefined,
      invoice: filter?.searchText || '',
      chasing: hasFilter(filter?.invoiceFilters, PAID_INVOICES)
        ? undefined
        : InvoiceService.chasingStatus(filter?.invoiceFilters),
      paid:
        hasFilter(filter?.invoiceFilters, PAID_INVOICES) ||
        hasFilter(filter?.invoiceFilters, COLLECTED_INVOICES),
      chaseFeedFilter: filter?.invoiceFilters?.find(f => f.type === CHASEFEED)?.data,
      invoiceIds: filter?.invoiceFilters?.find(f => f.type === INVOICEIDS)?.data,
      ...InvoiceService.collectingInvoiceStatus(filter?.invoiceFilters),
      ...(hasFilter(filter?.invoiceFilters, READY_FOR_COLLECTION) ? ALL_COLLECTIONS_INVOICES : {}),
      schedule: hasFilter(filter?.invoiceFilters, SCHEDULE)
        ? filter.invoiceFilters.filter(f => f.type === SCHEDULE).map(f => f.data)
        : undefined,
      ...InvoiceService.agedDebtFilter(filter?.invoiceFilters),
    };
  },
  collectingInvoiceStatus(filters = []) {
    const collectorStatus = [];
    const withoutCollectorStatus = [];
    const hasCollecting = hasFilter(filters, COLLECTING_INVOICES);
    const hasCollected = hasFilter(filters, COLLECTED_INVOICES);
    const hasPending = hasFilter(filters, PENDING_INVOICES);
    const hasChasing = hasFilter(filters, CHASING_INVOICES);
    const hasNotChasing = hasFilter(filters, NOT_CHASING_INVOICES);
    const hasWaitingContract = hasFilter(filters, WAITING_CONTRACT);

    if (hasCollecting) collectorStatus.push('COLLECTING');
    if (hasCollected) collectorStatus.push('COLLECTED');
    if (hasPending) collectorStatus.push('PENDING');
    if (hasWaitingContract) collectorStatus.push('WAITING_CONTRACT');
    if ((hasChasing || hasNotChasing) && (!hasCollecting || hasCollecting))
      withoutCollectorStatus.push('PENDING', 'COLLECTING', 'COLLECTED', 'WAITING_CONTRACT');

    return {
      collectorStatus: collectorStatus.length > 0 ? collectorStatus : undefined,
      withoutCollectorStatus:
        withoutCollectorStatus.length > 0 ? withoutCollectorStatus : undefined,
    };
  },
  chasingStatus(filters = []) {
    if (hasFilter(filters, PAID_INVOICES)) return undefined;
    if (!filters || !filters.length) return 'all';

    const hasChasing = hasFilter(filters, CHASING_INVOICES);
    const hasNotChasing = hasFilter(filters, NOT_CHASING_INVOICES);

    if (hasChasing && hasNotChasing) return 'all';
    if (hasChasing) return 'yes';
    if (hasNotChasing) return 'no';
    return 'all';
  },
  agedDebtFilter(selectedFilters = []) {
    const filters = selectedFilters.filter(f => f.type === AGED_DEBT).map(f => f.data);
    if (filters.length) {
      return {
        agedDebt: filters,
      };
    }
    return {};
  },
  async getInvoices({ limit, skip, sort, filter = { invoiceFilters: [] }, contactId, shortCode }) {
    const body = {
      query: `query (
        $filter: InvoiceFilter, $ContactID: String, $limit: Int,
        $skip: Int, $sort: String, $missingEmailAddress: Boolean,
        $missingNames: Boolean,
        ) {
        organisation {
          invoices(
            ContactID: $ContactID,
            skip: $skip,
            limit: $limit,
            sort: $sort,
            filter: $filter,
            missingEmailAddress: $missingEmailAddress,
            missingNames: $missingNames,
            ) {
            totalCount
            nodes {
              _id
              debtor {
                status
              }
              dispute {
                hash
                isOpen
                note
                reason
              }
              InvoiceID
              InvoiceNumber
              isPaymentPending
              AmountDue
              DueDate
              noChase
              AmountCredited
              ExpectedPaymentDate
              CachedExpectedPaymentDate
              AmountPaid
              CurrencyCode
              Status
              Total
              collector {
                status
                fullyCollectedOnDate
                agreedRate
                contractUrl
                caseId
              }
              paymentHistory {
                date
                provider
                event_data {
                  type
                  payment_method_types
                  paymentMethodType
                }
              }
              paymentPrediction {
                probabilityOfBeingPaidLate
              }
              debtor {
                status
              }
              chaseHistory {
                template
              }
              schedule {
                _id
                name
              }
              contact {
                _id
                Name
                FirstName
                LastName
                EmailAddress
                ContactID
                hasMissingEmail
                hasWrongEmail
                hasMissingName
                countryCode
                directDebit {
                  firstTimeAcceptedBecsAt,
                  firstTimeAcceptedBacsAt,
                  firstTimeAcceptedSepaAt,
                  firstTimeAcceptedAchAt,
                },
                collectionDetails {
                  notes
                  options {
                    latePaymentFees
                  }
                }
              }
              earlyPaymentDiscountValues {
                state
              }
              latePaymentFeesValues {
                state
              }
              FullyPaidOnDate
              Payments {
                Amount
                Date
              }
              paymentPlan {
                instalments {
                  total
                }
              }
            }
          }
        }
      }
    `,
      variables: {
        ContactID: contactId,
        limit,
        skip,
        sort,
        missingEmailAddress: hasFilter(filter?.invoiceFilters, MISSING_EMAIL),
        missingNames: hasFilter(filter?.invoiceFilters, MISSING_NAME),
        filter: InvoiceService.getInvoiceFilter(filter),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return (
      organisation && {
        Total: organisation.invoices.totalCount,
        Invoices:
          organisation.invoices.nodes?.map((invoice, index) => ({
            ...invoice,
            _id: invoice._id,
            id: index + parseInt(skip, 10),
            HasPaidPartial: invoice.AmountDue !== invoice.Total,
            FullyPaidOnDate:
              invoice.FullyPaidOnDate && formatDate(invoice.FullyPaidOnDate, { month: 'short' }),
            Total: invoice.Total,
            Name: invoice.contact ? invoice.contact.Name : '',
            CurrencyCode: invoice.CurrencyCode,
            LastTemplateUsed:
              invoice.chaseHistory && invoice.chaseHistory.length > 0
                ? invoice.chaseHistory[invoice.chaseHistory.length - 1].template
                : null,
            TotalChases:
              invoice.chaseHistory && invoice.chaseHistory.length ? invoice.chaseHistory.length : 0,
            DueDate: invoice.DueDate,
            InvoiceNumber: invoice.InvoiceNumber,
            Status: invoice.Status,
            Schedule: invoice.schedule,
          })) || [],
      }
    );
  },
  async getInvoiceContact({
    limit,
    skip,
    sort,
    filter = { invoiceFilters: [] },
    contactId,
    shortCode,
  }) {
    const body = {
      query: `query (
          $filter: InvoiceFilter, $ContactID: String, $limit: Int,
          $skip: Int, $sort: String, $missingEmailAddress: Boolean,
          $missingNames: Boolean,
          ) {
          organisation {
            invoices(
              ContactID: $ContactID,
              skip: $skip,
              limit: $limit,
              sort: $sort,
              filter: $filter,
              missingEmailAddress: $missingEmailAddress,
              missingNames: $missingNames,
              ) {
              totalCount
              nodes {
                _id
                Contact {
                  ContactID
                }
                contact {
                  _id
                  Name
                  FirstName
                  LastName
                  EmailAddress
                  ContactID
                  hasMissingEmail
                  hasWrongEmail
                  hasMissingName
                  countryCode
                  directDebit {
                    firstTimeAcceptedBecsAt,
                    firstTimeAcceptedBacsAt,
                    firstTimeAcceptedSepaAt,
                    firstTimeAcceptedAchAt,
                  },
                  collectionDetails {
                    notes
                    options {
                      latePaymentFees
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        ContactID: contactId,
        limit,
        skip,
        sort,
        missingEmailAddress: hasFilter(filter?.invoiceFilters, MISSING_EMAIL),
        missingNames: hasFilter(filter?.invoiceFilters, MISSING_NAME),
        filter: InvoiceService.getInvoiceFilter(filter),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return (
      organisation && {
        Total: organisation.invoices.totalCount,
        Invoices:
          organisation.invoices.nodes?.map(invoice => ({
            ...invoice,
            _id: invoice._id,
            ContactID: invoice.contact ? invoice.contact.ContactID : undefined,
            Contact: invoice.contact,
          })) || [],
      }
    );
  },
  async getInvoicesNextChases({
    limit,
    skip,
    sort,
    filter = { invoiceFilters: [] },
    contactId,
    shortCode,
  }) {
    const body = {
      query: `query (
        $filter: InvoiceFilter, $ContactID: String, $limit: Int,
        $skip: Int, $sort: String
        ) {
        organisation {
          invoices(
            ContactID: $ContactID,
            skip: $skip,
            limit: $limit,
            sort: $sort,
            filter: $filter,
            ) {
            totalCount
            nodes {
              _id
              InvoiceID
              InvoiceNumber
              nextChase {
                date
                number
                subject
                body
                template
                hasMissingEmail
                hasWrongEmail
                hasInvalidMobileNumber
                approximate
                endOfSchedule
                recipients {
                  to {
                    EmailAddress
                    PhoneNumber
                    FirstName
                    LastName
                  }
                  cc {
                    EmailAddress
                    FirstName
                    LastName
                  }
                }
                channel
              }
            }
          }
        }
      }
    `,
      variables: {
        ContactID: contactId,
        limit,
        skip,
        sort,
        filter: InvoiceService.getInvoiceFilter(filter),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);

    return (
      organisation && {
        Invoices:
          organisation.invoices.nodes?.map(invoice => ({
            ...invoice,
            _id: invoice._id,
            NextChase: invoice.nextChase ? invoice.nextChase : null,
          })) || [],
      }
    );
  },
  async get({ invoiceId, filters = {}, shortCode }) {
    const body = {
      query: `query ($invoiceId: String!, $paidInvoices: Boolean, $taskStatus: String) {
          organisation {
          invoice(InvoiceID: $invoiceId) {
            earlyPaymentDiscountValues {
              state
            }
            latePaymentFeesValues {
              state
            }
            paymentPlan {
              instalments {
                dueDate
                amountDue
                total
              }
            }
            tasks(status: $taskStatus) {
              nodes {
                _id
                type
              }
            }
            dispute {
              isOpen
              note
              reason
              hash
              createdBy {
                profile {
                  lastName
                  firstName
                }
              }
              history {
                isOpen
                note
                reason
                hash
                createdBy {
                  profile {
                    lastName
                    firstName
                  }
                }
                createdAt
              }
            }
            AmountDue
            _id
            CurrencyCode
            DueDate
            InvoiceID
            InvoiceNumber
            isPaymentPending
            Payments {
              Amount
              Date
            }
            ExpectedPaymentDate
            CachedExpectedPaymentDate
            Total
            Status
            Reference
            noChase
            accountingSoftwareUrl
            collector {
              status
              fullyCollectedOnDate
              agreedRate
              contractUrl
              caseId
            }
            debtor {
              status
            }
            schedule {
              name
              _id
            }
            contact {
              _id
              Name
              FirstName
              LastName
              EmailAddress
              ContactID
              hasMissingEmail
              hasWrongEmail
              hasMissingName
              invoices(paid: $paidInvoices) {
                _id
                InvoiceNumber
              }
              collectionDetails {
                notes
                options {
                  latePaymentFees
                }
              }
            }
            chaseHistory {
              date
              days
              template
              channel
              email {
                subject
                text
                to {
                  address
                  name
                }
                cc {
                  name
                  address
                }
                from {
                  address
                  name
                }
                attachments {
                  fileName
                }
              }
              sms {
                from
                to
                text
              }
              phoneCall {
                from
                to
                script
                status
              }
              chaseOpens {
                user
                date
                chase
              }
              chaseBounces {
                user
                date
                chase
              }
            }
            emails {
              messageId
              from {
                address
                name
              }
              to {
                address
                name
              }
              cc {
                name
                address
              }
              date
              subject
              text
              attachments {
                size
                path
                name
              }
            }
            notes {
              message
              date
              reminder
              hash
              user {
                profile {
                  firstName
                  lastName
                }
              }
              invoices {
                InvoiceID
                InvoiceNumber
              }
            }
            paymentMaxRetries
            invoicePdfLink
          }
        }
      }`,
      variables: {
        invoiceId,
        paidInvoices: filters?.paid,
        taskStatus: 'OPEN',
      },
    };

    const { data: result } = await ApiService.orgEndpoint(shortCode, {
      extractResponseData: false,
    }).post('/', { ...body });
    const { data, errors } = result;

    const { organisation } = data;
    const { invoice } = organisation;

    if ((errors && errors.length) || invoice === null) {
      throw new Error('Something went wrong retrieving the invoice!');
    }

    return invoice;
  },

  async getQuickView({ shortCode, invoiceId, users }) {
    const body = {
      query: `
        query ($invoiceId: String!) {
          organisation {
            invoice(InvoiceID: $invoiceId) {
              notes {
                message
                date
                reminder
                hash
                user {
                  profile {
                    firstName
                    lastName
                  }
                }
              }
              emails {
                messageId
                date
                from {
                  address
                  name
                }
                to {
                  address
                  name
                }
                subject
                text
              }
            }
          }
        }
      `,
      variables: { invoiceId },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    const { notes, emails } = data.organisation.invoice;
    const combined = [
      ...notes.map(note => ({
        ...note,
        type: 'note',
        message: note.message.replace('<', '&lt;').replace('>', '&gt;'),
      })),
      ...emails.map(email => ({
        ...email,
        type: 'email',
        isResponse:
          email.isResponse != null
            ? email.isResponse
            : users.findIndex(u => u.EmailAddress === email.from.address) > -1,
      })),
    ];

    combined.forEach(item => {
      item.dateTime = new Date(item.date).getTime();
    });

    combined.sort((m1, m2) => m2.dateTime - m1.dateTime);

    return combined.slice(0, 5);
  },
  async setExpectedPaymentDate(shortCode, invoiceIds, date, timezone) {
    const dateObj = new Date(date);
    const dateAsUtc = subMinutes(dateObj, dateObj.getTimezoneOffset()); // set Date to be as if it's UTC

    const body = {
      query: `mutation ($invoiceIds: [String!], $date: Date!) {
            setExpectedPaymentDate(invoiceIds: $invoiceIds, date: $date) {
              _id
              success
            }
          }`,
      variables: {
        invoiceIds,
        date: dateAsUtc.toISOString(),
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    const { setExpectedPaymentDate } = data;
    const xeroErrors = setExpectedPaymentDate.filter(i => !i.success).length;
    const saved = setExpectedPaymentDate.length;
    if (xeroErrors) {
      toast.error(
        `${pluralize('invoice', saved, true)} ${
          saved > 1 ? 'have' : 'has'
        } been saved in Chaser, but ${
          xeroErrors === saved ? '' : `${xeroErrors} of these `
        }could not be updated in Xero`,
      );
    }

    return data.setExpectedPaymentDate;
  },
  async addLatePaymentFeesToInvoice(shortCode, invoiceId) {
    const body = {
      query: `mutation ($invoiceId: String!) {
            addLatePaymentFeesToInvoice(invoiceId: $invoiceId)
          }`,
      variables: {
        invoiceId,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.addLatePaymentFeesToInvoice;
  },
  async removeLatePaymentFeesFromInvoice(shortCode, invoiceId) {
    const body = {
      query: `mutation ($invoiceId: String!) {
            removeLatePaymentFeesFromInvoice(invoiceId: $invoiceId)
          }`,
      variables: {
        invoiceId,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.removeLatePaymentFeesFromInvoice;
  },
  async addEarlyPaymentDiscountToInvoice(shortCode, invoiceId) {
    const body = {
      query: `mutation ($invoiceId: String!) {
            addEarlyPaymentDiscountToInvoice(invoiceId: $invoiceId)
          }`,
      variables: {
        invoiceId,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.addEarlyPaymentDiscountToInvoice;
  },
  async removeEarlyPaymentDiscountFromInvoice(shortCode, invoiceId) {
    const body = {
      query: `mutation ($invoiceId: String!) {
            removeEarlyPaymentDiscountFromInvoice(invoiceId: $invoiceId)
          }`,
      variables: {
        invoiceId,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.removeEarlyPaymentDiscountFromInvoice;
  },
  async unsetExpectedPaymentDate(shortCode, invoiceIds) {
    const body = {
      query: `mutation ($invoiceIds: [String!]) {
            unsetExpectedPaymentDate(invoiceIds: $invoiceIds) {
              _id
              success
            }
          }`,
      variables: {
        invoiceIds,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    const { unsetExpectedPaymentDate } = data;
    const xeroErrors = unsetExpectedPaymentDate.filter(i => !i.success).length;
    const saved = unsetExpectedPaymentDate.length;
    if (xeroErrors) {
      toast.error(
        `${pluralize('invoice', saved, true)} ${
          saved > 1 ? 'have' : 'has'
        } been removed from Chaser, but ${
          xeroErrors === saved ? '' : `${xeroErrors} of these `
        }could not be updated in Xero`,
      );
    }
    return data.unsetExpectedPaymentDate;
  },
  async getAllInvoices({ searchText = '', filters = [], contactId, shortCode }) {
    const body = {
      query: `query ($ContactID: String!, $filter: InvoiceFilter, $missingNames: Boolean, $missingEmailAddress: Boolean) {
        organisation {
          invoices(ContactID: $ContactID, filter: $filter, missingNames: $missingNames, missingEmailAddress: $missingEmailAddress) {
            nodes {
              _id
              InvoiceNumber
              CurrencyCode
              AmountDue
              DueDate
              Total
              isPaymentPending
              Contact {
                Name
                ContactID
              }
              contact {
                Name
                ContactID
                collectionDetails {
                  notes
                  options {
                    latePaymentFees
                  }
                }
              }
            }
          }
        }
      }`,
      variables: {
        ContactID: contactId || '',
        filter: InvoiceService.getInvoiceFilter({ invoiceFilters: filters, searchText }),
        missingNames: hasFilter(filters, MISSING_NAME),
        missingEmailAddress: hasFilter(filters, MISSING_EMAIL),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return get(organisation, 'invoices.nodes');
  },
  async getChaseNowData({ searchText, filters, contactId, shortCode }) {
    const body = {
      query: `query ($sort: String, $filter: InvoiceFilter, $ContactID: String, $missingNames: Boolean, $missingEmailAddress: Boolean) {
        organisation {
          invoices(filter: $filter, ContactID: $ContactID, missingNames: $missingNames, missingEmailAddress: $missingEmailAddress) {
            totalCount
            nodes {
              _id
              DueDate
              AmountDue
              CurrencyCode
              InvoiceID
              InvoiceNumber
              Contact {
                ContactID
                Name
              }
          }
        }
      }`,
      variables: {
        ContactID: contactId,
        filter: {
          inDispute: hasFilter(filters, IN_DISPUTE) ? true : undefined,
          invoice: searchText || '',
          chasing: InvoiceService.chasingStatus(filters),
          paid: hasFilter(filters, PAID_INVOICES),
          chaseFeedFilter: filters?.find(f => f.type === CHASEFEED)?.data,
          ...(hasFilter(filters, READY_FOR_COLLECTION) ? ALL_COLLECTIONS_INVOICES : {}),
          ...InvoiceService.collectingInvoiceStatus(filters),
          ...InvoiceService.agedDebtFilter(filters),
        },
        missingNames: hasFilter(filters, MISSING_NAME),
        missingEmailAddress: hasFilter(filters, MISSING_EMAIL),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return get(organisation, 'organisation.invoices.nodes');
  },
  async startChasingInvoices(shortCode, invoiceIds) {
    await batchFetch(
      ids =>
        ApiService.orgEndpoint(shortCode).post('/', {
          query: `mutation($invoiceIds: [String!]) {
            unpauseInvoices(invoiceIds: $invoiceIds)
          }`,
          variables: {
            invoiceIds: ids,
          },
        }),
      invoiceIds,
      { toastMessage: 'Updating invoices' },
    );
  },

  async stopChasingInvoices(shortCode, invoiceIds) {
    await batchFetch(
      ids =>
        ApiService.orgEndpoint(shortCode).post('/', {
          query: `mutation($invoiceIds: [String!]) {
            pauseInvoices(invoiceIds: $invoiceIds)
          }`,
          variables: {
            invoiceIds: ids,
          },
        }),
      invoiceIds,
      { toastMessage: 'Updating invoices' },
    );
  },
  async updateDispute({ shortCode, invoiceId, note, reason, isOpen }) {
    const body = {
      query: `mutation($invoiceId: String!, $note: String, $reason: String!, $isOpen: Boolean) {
        updateDispute(invoiceId: $invoiceId, note: $note, reason: $reason, isOpen: $isOpen)
      }`,
      variables: {
        invoiceId,
        note,
        reason,
        isOpen,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async getFilterCounts(shortCode) {
    const body = {
      query: `query($readyForCollectionFilter: InvoiceFilter) {
        organisation {
          name
          chasingInvoices: invoices(
            filter: {
              chasing: "yes"
            }
          ) {
            totalCount
          }
          notChasingInvoices: invoices(
            filter: {
              chasing: "no"
            }
          ) {
            totalCount
          },
          paidInvoices: invoices(
            filter: {
              paid: true
            }
          ) {
            totalCount
          }
          collectingInvoices: invoices(
            filter: {
              collectorStatus: "COLLECTING"
            }
          ) {
            totalCount
          }
          readyForCollection: invoices(filter: $readyForCollectionFilter) {
            totalCount
          }
          pendingInvoices: invoices(
            filter: {
              collectorStatus: "PENDING"
            }
          ) {
            totalCount
          }
          inDisputeInvoices: invoices(
            filter: {
              inDispute: true
            }
          ) {
            totalCount
          }
        }
      }`,
      variables: {
        readyForCollectionFilter: ALL_COLLECTIONS_INVOICES,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return {
      chasingInvoices: organisation.chasingInvoices.totalCount,
      notChasingInvoices: organisation.notChasingInvoices.totalCount,
      paidInvoices: organisation.paidInvoices.totalCount,
      collectingInvoices: organisation.collectingInvoices.totalCount,
      pendingInvoices: organisation.pendingInvoices.totalCount,
      readyForCollectionInvoices: organisation.readyForCollection.totalCount,
      inDisputeInvoices: organisation.inDisputeInvoices.totalCount,
    };
  },
  async createPaymentPlan(shortCode, { instalments, invoiceId }) {
    const body = {
      query: `mutation($invoiceId: String!, $instalments: [PaymentPlanInstalmentInput]) {
        createPaymentPlan(invoiceId: $invoiceId, instalments: $instalments)
      }`,
      variables: {
        invoiceId,
        instalments,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async cancelPaymentPlan(shortCode, invoiceId) {
    const body = {
      query: `mutation($invoiceId: String!) {
        cancelPaymentPlan(invoiceId: $invoiceId)
      }`,
      variables: {
        invoiceId,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async getInvoiceHistory({ shortCode, invoiceId }) {
    const body = {
      query: `query ($invoiceId: String!) {
            organisation {
            invoice(InvoiceID: $invoiceId) {
              InvoiceID
              Status
              InvoiceNumber
              tasks(status: "COMPLETED") {
                nodes {
                  type
                  completedBy {
                    profile {
                      firstName
                      lastName
                    }
                  }
                  createdDate
                  rescheduledDate
                  completedDate
                  metadata {
                    invoices {
                      InvoiceID
                      InvoiceNumber
                    }
                    result
                    notes
                    recordingUrl
                  }
                }
              }
              dispute {
                isOpen
                note
                reason
                hash
                createdBy {
                  profile {
                    lastName
                    firstName
                  }
                }
                history {
                  isOpen
                  note
                  reason
                  hash
                  createdBy {
                    profile {
                      lastName
                      firstName
                    }
                  }
                  createdAt
                }
              }
              contact {
                _id
                Name
                FirstName
                LastName
                EmailAddress
                ContactID
                hasMissingEmail
                hasWrongEmail
                hasMissingName
                invoices {
                  _id
                  InvoiceNumber
                }
                collectionDetails {
                  notes
                  options {
                    latePaymentFees
                  }
                }
              }
              chaseHistory {
                date
                days
                template
                channel
                phoneCall {
                  from
                  to
                  script
                  status
                }
                letter {
                  html
                  status
                  statusUpdatedAt
                }
                email {
                  subject
                  text
                  to {
                    address
                    name
                  }
                  cc {
                    name
                    address
                  }
                  bcc {
                    name
                    address
                  }
                  from {
                    address
                    name
                  }
                  attachments {
                    fileName
                  }
                }
                sms {
                  from
                  to
                  text
                }
                chaseOpens {
                  user
                  date
                  chase
                }
                chaseBounces {
                  user
                  date
                  chase
                }
              }
              emails {
                canReplyNowWithUserId
                messageId
                from {
                  address
                  name
                }
                to {
                  address
                  name
                }
                cc {
                  name
                  address
                }
                bcc {
                  name
                  address
                }
                date
                subject
                text
                attachments {
                  size
                  path
                  name
                }
              }
              notes {
                message
                date
                reminder
                hash
                user {
                  profile {
                    firstName
                    lastName
                  }
                }
                invoices {
                  InvoiceID
                  InvoiceNumber
                }
              }
              sentMonthlyStatements {
                _id
                email {
                  date
                  from {
                    name
                    address
                  }
                  to {
                    name
                    address
                  }
                  cc {
                    name
                    address
                  }
                  text
                  subject
                }
                emails {
                  date
                  from {
                    name
                    address
                  }
                  to {
                    name
                    address
                  }
                  cc {
                    name
                    address
                  }
                  subject
                  text
                }
              }
            }
          }
        }`,
      variables: {
        invoiceId,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return organisation.invoice;
  },
};

export default InvoiceService;
